import { DEFAULT_PAGE_SIZE } from '../../constants/App';
import * as types from '../types/hotels';

const initialState = {
    isLoading : false,
    data      : { pages: {}, hidePrices: null },
    filters   : {
        propertyName          : '',
        minPrice              : null,
        maxPrice              : null,
        stars                 : null,
        mealPlans             : null,
        propertyTypes         : null,
        countries             : null,
        distanceUnitOfMeasure : null,
    },
    autocompleteFilter  : null,
    priceRangeMax       : null,
    priceRangeMin       : null,
    associatedCountries : null,
    pagination          : {
        currentPage  : 1,
        totalPages   : 1,
        pageSize     : DEFAULT_PAGE_SIZE,
        totalResults : 1,
    },
    error            : false,
    isFirstTime      : true,
    sortBy           : '',
    classifications  : [],
    autocompleteData : {
        type  : '',
        value : '',
        code  : '',
    },
    coupon                 : '',
    googleMapsSessionToken : null,
    portalFilters          : null,
    showAlternativeOffers  : false,
};

const processChangeDisplay = (state, action) => (
    {
        ...state,
        data: {
            ...state.data,
            priceDisplayMode: action.payload,
        },
    }
);

const processSortBy = (state, actionPayload) => {
    const { recommendedSortCriteria } = actionPayload.settings;
    const sortByMap = {
        Classification : 'rating',
        Name           : 'name',
        default        : 'price',
    };

    return {
        ...state,
        sortBy: sortByMap[recommendedSortCriteria] || sortByMap.default,
    };
};

const processClassificationFilter = (state, action) => {
    const selectedClassificationFilter = `classification-${action.payload.code}`;

    return {
        ...state,
        filters: {
            ...state.filters,
            [selectedClassificationFilter]: action.payload.value,
        },
    };
};

const processAwardsFilter = (state, action) => {
    const selectedAwardFilter = `awards-${action.payload.code}`;

    return {
        ...state,
        filters: {
            ...state.filters,
            [selectedAwardFilter]: action.payload.value,
        },
    };
};

const getClassificationKey = (fullClassificationKey) => {
    const parts = fullClassificationKey.split(':');
    return `classification-${parts[1]}`;
};

const getResettedAutocompleteFilters = (state) => {
    const resettedFilters = {
        ...state.filters,
        propertyCode : undefined,
        location     : null,
        countries    : null,
        latitude     : null,
        longitude    : null,
        geoRegionId  : null,
    };

    if (state.searchBarClassificationKey) {
        resettedFilters[state.searchBarClassificationKey] = [];
    }

    return resettedFilters;
};

const hotels = (state = initialState, action = { type: null }) => {
    switch (action.type) {
        case 'HOTEL_LIST_START':
            return {
                ...state,
                isLoading: true,
            };
        case 'HOTEL_LIST_SUCCESS': {
            const newPage = { ...state.data.pages };
            newPage[action.payload.pages] = action.payload.properties;
            return {
                ...state,
                isLoading  : false,
                error      : false,
                pagination : {
                    ...state.pagination,
                    currentPage  : action.payload.page,
                    totalPages   : action.payload.pages || 1,
                    totalResults : action.payload.totalResults,
                },
                data: {
                    ...state.data,
                    ...action.payload,
                    hidePrices: action.payload.hidePrices,
                },
            };
        }

        case 'HOTEL_LIST_ERROR':
            return {
                ...state,
                pagination: {
                    ...state.pagination,
                    currentPage  : 1,
                    totalPages   : 1,
                    totalResults : 1,
                },
                isLoading : false,
                payload   : action.payload,
                error     : true,
            };
        case 'CHANGE_DISPLAY_MODE':
            return processChangeDisplay(state, action);

        case 'SET_CURRENT_PAGE_PAGINATION':
            return {
                ...state,
                pagination: {
                    ...state.pagination,
                    currentPage: action.payload,
                },
            };
        case 'SET_PAGE_SIZE':
            return {
                ...state,
                pagination: {
                    ...state.pagination,
                    currentPage : 1,
                    pageSize    : action.payload,
                },
            };
        case 'RESET_PAGINATION':
            return {
                ...state,
                pagination: {
                    ...state.pagination,
                    currentPage  : 1,
                    totalPages   : 1,
                    totalResults : 1,
                },
            };
        case 'CLEAN_PAGES':
            return {
                ...state,
                offers: {
                    ...state.offers,
                    data: {
                        pages         : {},
                        priceRangeMin : null,
                        priceRangeMax : null,
                    },
                },
            };
        case types.PORTAL_FILTERS:
            return {
                ...state,
                portalFilters: action.payload,
            };
        case types.GET_SUGGESTIONS_REQUEST_START:
        case types.GET_SUGGESTIONS_REQUEST_SUCCESS:
        case types.GET_SUGGESTIONS_REQUEST_FAILURE:
            return state;
        case types.CHANGE_PROPERTY_CODE_FILTER:
            return {
                ...state,
                filters: {
                    ...state.filters,
                    propertyCode: action.payload,
                },
            };

        case types.CHANGE_PROPERTY_LOCATION_FILTER:
            return {
                ...state,
                filters: {
                    countries : [],
                    location  : action.payload,
                },
            };
        case types.CHANGE_PROPERTY_NAME_FILTER:
            return {
                ...state,
                filters: {
                    ...state.filters,
                    location     : '',
                    propertyName : action.payload,
                },
            };
        case types.CHANGE_PROPERTY_NAME_FILTER_TEMP:
            return {
                ...state,
                filtersTemp: {
                    ...state.filtersTemp,
                    propertyName: action.payload,
                },
            };
        case types.CHANGE_PROPERTY_COUNTRY_FILTER:
            return {
                ...state,
                filters: {
                    ...state.filters,
                    countries: action.payload,
                },
            };
        case types.CHANGE_PROPERTY_CLASSIFICATION_FILTER:
            return {
                ...state,
                searchBarClassificationKey : getClassificationKey(action.payload),
                filters                    : {
                    ...state.filters,
                    [getClassificationKey(action.payload)]: [ action.payload ],
                },
            };
        case types.CHANGE_PRICE_FILTER:
            return {
                ...state,
                filters: {
                    ...state.filters,
                    minPrice : action.payload.minPrice,
                    maxPrice : action.payload.maxPrice,
                },
            };
        case types.CHANGE_STARS_FILTER:
            return {
                ...state,
                filters: {
                    ...state.filters,
                    stars: action.payload,
                },
            };
        case types.CHANGE_MEALPLANS_FILTER:
            return {
                ...state,
                filters: {
                    ...state.filters,
                    mealPlans: action.payload,
                },
            };
        case types.CHANGE_PROPERTYTYPES_FILTER:
            return {
                ...state,
                filters: {
                    ...state.filters,
                    propertyTypes: action.payload,
                },
            };
        case types.SET_PROPERTIES_PRICES_RANGE:
            return {
                ...state,
                priceRangeMin : action.payload.priceRangeMin,
                priceRangeMax : action.payload.priceRangeMax,

            };

        case types.RESET_PROPERTIES_FILTERS:
            return {
                ...state,
                filters: {
                    propertyName  : '',
                    minPrice      : null,
                    maxPrice      : null,
                    stars         : null,
                    mealPlans     : null,
                    propertyTypes : null,
                    countries     : null,
                },
            };
        case types.RESET_AUTOCOMPLETE_FILTERS:
            return {
                ...state,
                filters: getResettedAutocompleteFilters(state),
            };

        case types.SET_AUTOCOMPLETE_FILTER:
            return {
                ...state,
                autocompleteFilter : action.payload,
                autocompleteData   : {
                    ...state.autocompleteData,
                    ...action.autocompleteData,
                },
            };
        case types.SET_IS_FIRST_TIME:
            return {
                ...state,
                isFirstTime: false,
            };
        case types.SET_ASSOCIATED_COUNTRIES:
            return {
                ...state,
                associatedCountries: action.payload,
            };
        case types.SORT_PROPERTIES:
            return {
                ...state,
                sortBy: action.payload,
            };
        case types.SET_CLASSIFICATIONS:
            return {
                ...state,
                classifications: action.payload,
            };
        case types.APPLY_COUPON:
            return {
                ...state,
                coupon: action.payload,
            };

        case types.CHANGE_GOOGLE_MAPS_SESSION_TOKEN:
            return {
                ...state,
                googleMapsSessionToken: action.payload,
            };
        case types.SET_GEO_FILTERS:
            return {
                ...state,
                filters: {
                    ...state.filters,
                    ...action.payload,
                },
                googleMapsSessionToken: null,
            };
        case types.RESET_GEO_FILTERS:
            return {
                ...state,
                filters: {},
            };
        case types.CHANGE_COUNTRIES_FILTER:
            return {
                ...state,
                filters: {
                    ...state.filters,
                    countries: action.payload,
                },
            };
        case types.CHANGE_CLASSIFICATION_FILTER:
            return processClassificationFilter(state, action);
        case types.CHANGE_AWARDS_FILTER:
            return processAwardsFilter(state, action);
        case 'DATA_REQUEST_SUCCESS':
            return processSortBy(state, action.payload);
        case 'UPDATE_ALTERNATIVE_OFFERS':
            return {
                ...state,
                showAlternativeOffers: action.payload.active
                        && (action.payload.filterAndSearchSetting === 'defaultOn' || action.payload.filterAndSearchSetting === 'alwaysOn'),
            };
        case types.TOGGLE_ALTERNATIVE_OFFERS_SHOWN:
            return {
                ...state,
                showAlternativeOffers: !state.showAlternativeOffers,
            };
        default:
            return state;
    }
};

export default hotels;
